/* eslint-disable max-len */
import { createSlice, combineReducers } from '@reduxjs/toolkit';
import dataReducer from '../features/dataSlices';
import feedReducer from './feedSlice';
import miscReducer from './miscSlice';

const toMutableNode = (node) => {
  if (!node || typeof node !== 'object') return node;
  return {
    ...node,
    data: node?.data && typeof node.data === 'object' ? { ...node.data } : node?.data,
    position: node?.position && typeof node.position === 'object' ? { ...node.position } : node?.position,
    style: node?.style && typeof node.style === 'object' ? { ...node.style } : node?.style,
    selected: Boolean(node?.selected)
  };
};

const toMutableEdge = (edge) => {
  if (!edge || typeof edge !== 'object') return edge;
  return {
    ...edge,
    data: edge?.data && typeof edge.data === 'object' ? { ...edge.data } : edge?.data,
    style: edge?.style && typeof edge.style === 'object' ? { ...edge.style } : edge?.style,
    selected: Boolean(edge?.selected)
  };
};

const toMutableNodes = (nodes = []) => (Array.isArray(nodes) ? nodes.map(toMutableNode) : []);
const toMutableEdges = (edges = []) => (Array.isArray(edges) ? edges.map(toMutableEdge) : []);
const isSameNodes = (prev = [], next = []) => {
  if (prev === next) return true;
  if (!Array.isArray(prev) || !Array.isArray(next)) return false;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i += 1) {
    const a = prev[i];
    const b = next[i];
    if (
      a?.id !== b?.id ||
      a?.name !== b?.name ||
      a?.type !== b?.type ||
      a?.selected !== b?.selected ||
      a?.position?.x !== b?.position?.x ||
      a?.position?.y !== b?.position?.y
    ) {
      return false;
    }
  }
  return true;
};

const isSameEdges = (prev = [], next = []) => {
  if (prev === next) return true;
  if (!Array.isArray(prev) || !Array.isArray(next)) return false;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i += 1) {
    const a = prev[i];
    const b = next[i];
    if (
      a?.id !== b?.id ||
      a?.source !== b?.source ||
      a?.target !== b?.target ||
      a?.sourceHandle !== b?.sourceHandle ||
      a?.targetHandle !== b?.targetHandle ||
      a?.selected !== b?.selected
    ) {
      return false;
    }
  }
  return true;
};

// Canvas initial state
const canvasInitialState = {
  elements: [],
  edges: [],
  text: '',
  checked: false,
  flowSheetExecuted: false,
  streamNum_dict: {},
  unitopNames: {} // Stores unitop names by ID: { 'feed_1': 'Feed_1', 'ro_1': 'ROHP_1', ... }
};

// Canvas reducer
const canvasReducer = (state = canvasInitialState, action) => {
  switch (action.type) {
  case 'canvas/setElements':
    return { ...state, elements: action.payload };
  case 'canvas/setEdges':
    return { ...state, edges: action.payload };
  case 'canvas/setText':
    return { ...state, text: action.payload };
  case 'canvas/setChecked':
    return { ...state, checked: action.payload };
  case 'canvas/setStreamNumDict':
    return { ...state, streamNum_dict: action.payload && typeof action.payload === 'object' ? action.payload : {} };
  case 'canvas/setAll':
    return { ...state, ...action.payload };
  case 'canvas/setUnitopName':
    return { ...state, unitopNames: { ...state.unitopNames, [action.payload.id]: action.payload.name } };
  case 'canvas/setUnitopNames':
    return { ...state, unitopNames: { ...(action.payload || {}) } };
  case 'canvas/removeUnitopName':
    // eslint-disable-next-line no-case-declarations
    const { [action.payload]: removed, ...remainingNames } = state.unitopNames;
    return { ...state, unitopNames: remainingNames };
  case 'canvas/clearUnitopNames':
    return { ...state, unitopNames: {} };
  case 'canvas/reset':
    return canvasInitialState;
  default:
    return state;
  }
};

// Combine all reducers for a single tab's state
const tabReducer = combineReducers({
  feed: feedReducer,
  misc: miscReducer,
  data: dataReducer,
  canvas: canvasReducer,
});

// Get the initial state for a single tab
export const getTabInitialState = () => tabReducer(undefined, { type: '@@INIT' });

// Initial state with a default tab
const initialState = {
  activeTabId: 'tab-1',
  tabOrder: ['tab-1'],
  tabNames: {
    'tab-1': 'Tab 1'
  },
  tabStates: {
    'tab-1': getTabInitialState()
  }
};

const tabSlice = createSlice({
  name: 'tabs',
  initialState,
  reducers: {
    // Create a new tab with fresh initial state
    createTab: (state, action) => {
      const { tabId, tabName = `Tab ${state.tabOrder.length + 1}` } = action.payload;
      if (!state.tabStates[tabId]) {
        state.tabStates[tabId] = getTabInitialState();
        state.tabOrder.push(tabId);
        state.tabNames[tabId] = tabName;
        state.activeTabId = tabId;
      }
    },

    // Remove a tab
    removeTab: (state, action) => {
      const tabId = action.payload;
      if (state.tabStates[tabId] && state.tabOrder.length > 1) {
        delete state.tabStates[tabId];
        delete state.tabNames[tabId];
        state.tabOrder = state.tabOrder.filter(id => id !== tabId);
        // If removing active tab, switch to another
        if (state.activeTabId === tabId) {
          state.activeTabId = state.tabOrder[0];
        }
      }
    },

    // Switch to a different tab
    setActiveTab: (state, action) => {
      const tabId = action.payload;
      if (state.tabStates[tabId]) {
        state.activeTabId = tabId;
      }
    },

    // Rename a tab
    renameTab: (state, action) => {
      const { tabId, tabName } = action.payload;
      if (state.tabNames[tabId]) {
        state.tabNames[tabId] = tabName;
      }
    },

    // Reset a specific tab to initial state
    resetTab: (state, action) => {
      const tabId = action.payload;
      if (state.tabStates[tabId]) {
        state.tabStates[tabId] = getTabInitialState();
      }
    },

    // Reset all tabs to initial state
    resetAllTabs: (state) => {
      Object.keys(state.tabStates).forEach(tabId => {
        state.tabStates[tabId] = getTabInitialState();
      });
    },

    // Reset entire tabs slice to initial state (removes all tabs except default)
    resetToInitialState: () => {
      return {
        ...initialState,
        tabStates: {
          'tab-1': getTabInitialState()
        }
      };
    },

    // Duplicate a tab
    duplicateTab: (state, action) => {
      const { sourceTabId, newTabId, newTabName } = action.payload;
      if (state.tabStates[sourceTabId] && !state.tabStates[newTabId]) {
        state.tabStates[newTabId] = JSON.parse(JSON.stringify(state.tabStates[sourceTabId]));
        state.tabOrder.push(newTabId);
        state.tabNames[newTabId] = newTabName || `${state.tabNames[sourceTabId]} (Copy)`;
        state.activeTabId = newTabId;
      }
    },

    // Update canvas state for a specific tab
    updateTabCanvas: (state, action) => {
      const { tabId, elements, edges, text, checked, flowSheetExecuted, streamNum_dict } = action.payload;
      if (state.tabStates[tabId]) {
        if (!state.tabStates[tabId].canvas) {
          state.tabStates[tabId].canvas = { elements: [], edges: [], text: '', checked: false, flowSheetExecuted: false, streamNum_dict: {}, unitopNames: {} };
        }
        if (elements !== undefined) {
          const nextElements = toMutableNodes(elements);
          if (!isSameNodes(state.tabStates[tabId].canvas.elements, nextElements)) {
            state.tabStates[tabId].canvas.elements = nextElements;
          }
        }
        if (edges !== undefined) {
          const nextEdges = toMutableEdges(edges);
          if (!isSameEdges(state.tabStates[tabId].canvas.edges, nextEdges)) {
            state.tabStates[tabId].canvas.edges = nextEdges;
          }
        }
        if (text !== undefined && state.tabStates[tabId].canvas.text !== text) {
          state.tabStates[tabId].canvas.text = text;
        }
        if (checked !== undefined && state.tabStates[tabId].canvas.checked !== checked) {
          state.tabStates[tabId].canvas.checked = checked;
        }
        if (flowSheetExecuted !== undefined && state.tabStates[tabId].canvas.flowSheetExecuted !== flowSheetExecuted) {
          state.tabStates[tabId].canvas.flowSheetExecuted = Boolean(flowSheetExecuted);
        }
        if (streamNum_dict !== undefined) {
          state.tabStates[tabId].canvas.streamNum_dict = (streamNum_dict && typeof streamNum_dict === 'object') ? streamNum_dict : {};
        }
      }
    },

    // Update canvas state for the active tab
    updateActiveTabCanvas: (state, action) => {
      const { elements, edges, text, checked, flowSheetExecuted, streamNum_dict } = action.payload;
      const tabId = state.activeTabId;
      if (state.tabStates[tabId]) {
        if (!state.tabStates[tabId].canvas) {
          state.tabStates[tabId].canvas = { elements: [], edges: [], text: '', checked: false, flowSheetExecuted: false, streamNum_dict: {}, unitopNames: {} };
        }
        if (elements !== undefined) {
          const nextElements = toMutableNodes(elements);
          if (!isSameNodes(state.tabStates[tabId].canvas.elements, nextElements)) {
            state.tabStates[tabId].canvas.elements = nextElements;
          }
        }
        if (edges !== undefined) {
          const nextEdges = toMutableEdges(edges);
          if (!isSameEdges(state.tabStates[tabId].canvas.edges, nextEdges)) {
            state.tabStates[tabId].canvas.edges = nextEdges;
          }
        }
        if (text !== undefined && state.tabStates[tabId].canvas.text !== text) {
          state.tabStates[tabId].canvas.text = text;
        }
        if (checked !== undefined && state.tabStates[tabId].canvas.checked !== checked) {
          state.tabStates[tabId].canvas.checked = checked;
        }
        if (flowSheetExecuted !== undefined && state.tabStates[tabId].canvas.flowSheetExecuted !== flowSheetExecuted) {
          state.tabStates[tabId].canvas.flowSheetExecuted = Boolean(flowSheetExecuted);
        }
        if (streamNum_dict !== undefined) {
          state.tabStates[tabId].canvas.streamNum_dict = (streamNum_dict && typeof streamNum_dict === 'object') ? streamNum_dict : {};
        }
      }
    },

    // Set flow execution status for active tab
    setActiveTabFlowSheetExecuted: (state, action) => {
      const tabId = state.activeTabId;
      if (state.tabStates[tabId]) {
        if (!state.tabStates[tabId].canvas) {
          state.tabStates[tabId].canvas = { elements: [], edges: [], text: '', checked: false, flowSheetExecuted: false, streamNum_dict: {}, unitopNames: {} };
        }
        state.tabStates[tabId].canvas.flowSheetExecuted = Boolean(action.payload);
      }
    },

    // Set flow execution status for specific tab
    setTabFlowSheetExecuted: (state, action) => {
      const { tabId, flowSheetExecuted } = action.payload || {};
      if (tabId && state.tabStates[tabId]) {
        if (!state.tabStates[tabId].canvas) {
          state.tabStates[tabId].canvas = { elements: [], edges: [], text: '', checked: false, flowSheetExecuted: false, streamNum_dict: {}, unitopNames: {} };
        }
        state.tabStates[tabId].canvas.flowSheetExecuted = Boolean(flowSheetExecuted);
      }
    },

    // Set auto-stream ID toggle (checked) for the active tab canvas
    setCanvasChecked: (state, action) => {
      const tabId = state.activeTabId;
      if (state.tabStates[tabId]) {
        if (!state.tabStates[tabId].canvas) {
          state.tabStates[tabId].canvas = { elements: [], edges: [], text: '', checked: false, flowSheetExecuted: false, streamNum_dict: {}, unitopNames: {} };
        }
        state.tabStates[tabId].canvas.checked = Boolean(action.payload);
      }
    },

    // Set stream number dictionary for active tab canvas
    setCanvasStreamNumDict: (state, action) => {
      const tabId = state.activeTabId;
      if (state.tabStates[tabId]) {
        if (!state.tabStates[tabId].canvas) {
          state.tabStates[tabId].canvas = { elements: [], edges: [], text: '', checked: false, flowSheetExecuted: false, streamNum_dict: {}, unitopNames: {} };
        }
        state.tabStates[tabId].canvas.streamNum_dict = (action.payload && typeof action.payload === 'object')
          ? action.payload
          : {};
      }
    },

    // Set a single unitop name for the active tab
    setUnitopName: (state, action) => {
      const { id, name } = action.payload;
      const tabId = state.activeTabId;
      if (state.tabStates[tabId]) {
        if (!state.tabStates[tabId].canvas) {
          state.tabStates[tabId].canvas = { elements: [], edges: [], text: '', checked: false, flowSheetExecuted: false, streamNum_dict: {}, unitopNames: {} };
        }
        if (!state.tabStates[tabId].canvas.unitopNames) {
          state.tabStates[tabId].canvas.unitopNames = {};
        }
        state.tabStates[tabId].canvas.unitopNames[id] = name;
      }
    },

    // Set multiple unitop names for the active tab (bulk update)
    setUnitopNames: (state, action) => {
      const namesObj = action.payload;
      const tabId = state.activeTabId;
      if (state.tabStates[tabId]) {
        if (!state.tabStates[tabId].canvas) {
          state.tabStates[tabId].canvas = { elements: [], edges: [], text: '', checked: false, flowSheetExecuted: false, streamNum_dict: {}, unitopNames: {} };
        }
        if (!state.tabStates[tabId].canvas.unitopNames) {
          state.tabStates[tabId].canvas.unitopNames = {};
        }
        state.tabStates[tabId].canvas.unitopNames = { ...(namesObj || {}) };
      }
    },

    // Remove a unitop name from the active tab
    removeUnitopName: (state, action) => {
      const id = action.payload;
      const tabId = state.activeTabId;
      if (state.tabStates[tabId]?.canvas?.unitopNames) {
        delete state.tabStates[tabId].canvas.unitopNames[id];
      }
    },

    // Clear all unitop names for the active tab
    clearUnitopNames: (state) => {
      const tabId = state.activeTabId;
      if (state.tabStates[tabId]?.canvas) {
        state.tabStates[tabId].canvas.unitopNames = {};
      }
    }
  },
  extraReducers: (builder) => {
    // Handle tab-scoped actions
    builder.addMatcher(
      (action) => action.type === 'tabs/tabAction',
      (state, action) => {
        const { tabId, action: innerAction } = action.payload;
        if (state.tabStates[tabId]) {
          // Apply the inner action to the specific tab's state
          state.tabStates[tabId] = tabReducer(state.tabStates[tabId], innerAction);
        }
      }
    );
  }
});

export const {
  createTab,
  removeTab,
  setActiveTab,
  renameTab,
  resetTab,
  resetAllTabs,
  resetToInitialState,
  duplicateTab,
  updateTabCanvas,
  updateActiveTabCanvas,
  setActiveTabFlowSheetExecuted,
  setTabFlowSheetExecuted,
  setCanvasChecked,
  setCanvasStreamNumDict,
  setUnitopName,
  setUnitopNames,
  removeUnitopName,
  clearUnitopNames
} = tabSlice.actions;

// Selector to get active tab's state
export const selectActiveTabState = (state) => state.tabs.tabStates[state.tabs.activeTabId];

// Selector to get active tab's canvas state
export const selectActiveTabCanvas = (state) => {
  const tabState = state.tabs.tabStates[state.tabs.activeTabId];
  return tabState?.canvas || { elements: [], edges: [], text: '', checked: false, flowSheetExecuted: false, streamNum_dict: {}, unitopNames: {} };
};

// Selector to get a specific tab's canvas state
export const selectTabCanvas = (tabId) => (state) => {
  const tabState = state.tabs.tabStates[tabId];
  return tabState?.canvas || { elements: [], edges: [], text: '', checked: false, flowSheetExecuted: false, streamNum_dict: {}, unitopNames: {} };
};

// Selector to get all tab canvases
export const selectAllTabCanvases = (state) => {
  const canvases = {};
  Object.keys(state.tabs.tabStates).forEach(tabId => {
    canvases[tabId] = state.tabs.tabStates[tabId]?.canvas || { elements: [], edges: [], text: '', checked: false, flowSheetExecuted: false, streamNum_dict: {}, unitopNames: {} };
  });
  return canvases;
};

// Selector to get auto-stream ID toggle for active tab canvas
export const selectActiveTabAutoStremId = (state) => {
  const tabState = state.tabs.tabStates[state.tabs.activeTabId];
  return Boolean(tabState?.canvas?.checked);
};

// Selector to get stream number dictionary for active tab canvas
export const selectActiveTabStreamNumDict = (state) => {
  const tabState = state.tabs.tabStates[state.tabs.activeTabId];
  return tabState?.canvas?.streamNum_dict || {};
};

// Selector to get flow execution status for active tab
export const selectActiveTabFlowSheetExecuted = (state) => {
  const tabState = state.tabs.tabStates[state.tabs.activeTabId];
  return Boolean(tabState?.canvas?.flowSheetExecuted);
};

// Selector to get flow execution status for a specific tab
export const selectTabFlowSheetExecuted = (tabId) => (state) => {
  const tabState = state.tabs.tabStates[tabId];
  return Boolean(tabState?.canvas?.flowSheetExecuted);
};

// Selector to get a specific tab's state
export const selectTabState = (tabId) => (state) => state.tabs.tabStates[tabId];

// Selector to get active tab ID
export const selectActiveTabId = (state) => state.tabs.activeTabId;

// Selector to get all tab IDs in order
export const selectTabOrder = (state) => state.tabs.tabOrder;

// Selector to get tab names
export const selectTabNames = (state) => state.tabs.tabNames;

// Selector to get all unitop names for the active tab
export const selectActiveTabUnitopNames = (state) => {
  const tabState = state.tabs.tabStates[state.tabs.activeTabId];
  return tabState?.canvas?.unitopNames || {};
};

// Selector to get a specific unitop name for the active tab
export const selectUnitopName = (id) => (state) => {
  const tabState = state.tabs.tabStates[state.tabs.activeTabId];
  return tabState?.canvas?.unitopNames?.[id] || null;
};

// Selector to get all unitop names for a specific tab
export const selectTabUnitopNames = (tabId) => (state) => {
  const tabState = state.tabs.tabStates[tabId];
  return tabState?.canvas?.unitopNames || {};
};

export default tabSlice.reducer;

// Export the combined tab reducer for use in dispatching actions to specific tabs
export { tabReducer };
